import { t } from 'i18next';

import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';
import { htmlNodes } from './htmlNodes';

export type InventoryContent = { html: string; text: string };

export interface BuildInventoryContentParams {
  inventory?: ConversationInventory | FormattedConversationInventory;
}

export const buildInventoryContent = ({
  inventory,
}: BuildInventoryContentParams = {}): InventoryContent => {
  const insightTitles =
    inventory && 'insightTitles' in inventory
      ? inventory.insightTitles
      : undefined;
  let html = '';
  let text = '';

  if (!inventory || Object.keys(inventory).length === 0) {
    return { html, text };
  }

  const entities = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const timeseries = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const webLinks =
    inventory?.['web-links']?.items?.flatMap(i => i.urls ?? []) ?? [];
  const financialStatements =
    inventory?.['financial-statement']?.items?.map(i => i.tag) ?? [];
  const externalData =
    inventory?.['external-data']?.items?.flatMap(i => i.links ?? []) ?? [];

  const insightHeadlines =
    inventory?.insight?.items?.map(
      item => insightTitles?.[item.id] ?? item.id
    ) ?? [];

  const documentSearchItems =
    inventory?.['document-search']?.items?.filter(
      item => (item.documents?.length ?? 0) > 0
    ) ?? [];

  const citationLines = documentSearchItems.map(item => {
    const rawQuestion = item.question ?? '';
    const question = rawQuestion ? `"${rawQuestion}"` : '';
    const count = item.documents?.length ?? 0;
    return t('chat:inventory.citationsForQuestionFoundDocuments', {
      question,
      count,
    });
  });

  const sections = [
    {
      title: t('chat:inventory.sections.entities', { count: entities.length }),
      textItems: entities,
      htmlItems: entities,
    },
    {
      title: t('chat:inventory.sections.timeSeries', {
        count: timeseries.length,
      }),
      textItems: timeseries,
      htmlItems: timeseries,
    },
    {
      title: t('chat:inventory.sections.financials', {
        count: financialStatements.length,
      }),
      textItems: financialStatements,
      htmlItems: financialStatements,
    },
    {
      title: t('chat:inventory.sections.citations', {
        count: citationLines.length,
      }),
      textItems: citationLines,
      htmlItems: citationLines,
    },
    {
      title: t('chat:inventory.sections.insights', {
        count: insightHeadlines.length,
      }),
      textItems: insightHeadlines,
      htmlItems: insightHeadlines,
    },
    {
      title: t('chat:inventory.sections.externalData', {
        count: externalData.length,
      }),
      textItems: externalData,
      htmlItems: externalData
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
    {
      title: t('chat:inventory.sections.webLinks', { count: webLinks.length }),
      textItems: webLinks,
      htmlItems: webLinks
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
  ];

  const nonEmpty = sections.filter(s => s.textItems.length > 0);

  if (!nonEmpty.length) {
    return { html, text };
  }

  text += `\n\n## ${t('chat:inventory.sources')}`;

  html += htmlNodes.h2(t('chat:inventory.sources'));
  nonEmpty.forEach(s => {
    text += `\n\n### ${s.title}\n` + s.textItems.map(v => `- ${v}`).join('\n');
    html += htmlNodes.h3(s.title);
    html += htmlNodes.unorderedList(
      s.htmlItems
        .filter(v => v !== null && v !== undefined)
        .map(v => htmlNodes.unorderedListItem(v))
        .join('')
    );
  });

  return { html, text };
};
